/**
 * Shared fog-mask uniforms (KTS "hide the unexplored" pass, 2026-07-03).
 *
 * The fog shroud (fog.ts) already darkens the ground per fog tier. But in a game
 * with real exploration (unlike Dota/LoL where the map layout is always known) the
 * never-explored frontier should hide EVERYTHING standing on it — trees, rocks,
 * resource nodes — not just tint the dirt. This module is the seam that lets the
 * doodad + resource-node toon materials read the SAME vision field the shroud uses
 * and dissolve their fragments where the ground has never been scouted.
 *
 * Mirrors `canopy-fade.ts`: a single module-level uniform block that every
 * fog-maskable material binds at shader-compile time (see `toon.ts` `fogMask`
 * option), so one per-frame update in fog.ts drives them all at once.
 *
 *   uFogMask       RG DataTexture owned by fog.ts. G = explored-ness (0..1),
 *                  spatially feathered + temporally eased, sampled by world-XZ.
 *   uFogMaskOrigin world coord of the mask's (0,0) corner.
 *   uFogMaskExtent world units the mask spans (square).
 *   uFogMaskEnabled 0 until fog.ts has computed vision at least once — until then
 *                  materials render un-masked, so the world is never briefly blank
 *                  on load (before any cell is explored the whole field reads 0).
 *
 * A 1×1 fully-explored placeholder is bound at import time so a material that
 * compiles BEFORE `createFog()` runs samples a valid (always-visible) texture;
 * fog.ts swaps `.value` to the live texture and flips `uFogMaskEnabled` on.
 */
import * as THREE from "three";

const placeholder = new THREE.DataTexture(
  new Uint8Array([255, 255]), // R=visible, G=explored → 1,1 = fully revealed
  1,
  1,
  THREE.RGFormat,
  THREE.UnsignedByteType,
);
placeholder.needsUpdate = true;

export const fogMaskUniforms = {
  uFogMask: { value: placeholder as THREE.Texture },
  uFogMaskOrigin: { value: new THREE.Vector2(0, 0) },
  uFogMaskExtent: { value: 1 },
  uFogMaskEnabled: { value: 0 },
};
